import { useEffect, useMemo, useState } from 'react';

import { useDashboardStore } from '@/state/store';

/**
 * Friday celebration modal.
 *
 * Rendered above the rest of the app whenever the store reports
 * ``celebration.active``.  Shows a three-tier podium with the
 * winner in the centre and gold/silver/bronze tinted blocks for
 * ranks 1/2/3.  Confetti pieces rain from the top of the modal --
 * pure CSS keyframes so no Pixi context required.
 *
 * Behaviour:
 *   * Dismissable via the "close" button or the Escape key.
 *   * Auto-dismisses 60 s after ``startedAt`` if the store hasn't
 *     emitted ``celebration.weekly_ended`` by then -- matches the
 *     ``scene_hint.duration_ms = 60_000`` the backend sends for the
 *     winners reel (see ``DASHBOARD_EVENT_SEQUENCES.md``).
 */
const PODIUM_BG: Record<1 | 2 | 3, string> = {
  1: 'linear-gradient(180deg, #ffd33d, #b88c0e)',
  2: 'linear-gradient(180deg, #d0d7de, #6e7681)',
  3: 'linear-gradient(180deg, #cd7f32, #6d3a13)',
};

const PODIUM_HEIGHT: Record<1 | 2 | 3, number> = {
  1: 180,
  2: 130,
  3: 110,
};

const PODIUM_ORDER: ReadonlyArray<1 | 2 | 3> = [2, 1, 3]; // visually L–M–R

export function FridayCelebrationOverlay() {
  const celebration = useDashboardStore((s) => s.celebration);
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Reset the dismiss latch whenever a new celebration starts.
  useEffect(() => {
    if (celebration.active) setDismissed(false);
  }, [celebration.startedAt, celebration.active]);

  // Tick every second so the auto-dismiss countdown is accurate.
  useEffect(() => {
    if (!celebration.active || dismissed) return;
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, [celebration.active, dismissed]);

  // Esc -> dismiss.
  useEffect(() => {
    if (!celebration.active || dismissed) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [celebration.active, dismissed]);

  const startedMs = celebration.startedAt
    ? new Date(celebration.startedAt).getTime()
    : null;
  const autoDismissAt = startedMs ? startedMs + 60_000 : null;
  if (autoDismissAt && now > autoDismissAt && !dismissed) {
    // schedule via setTimeout to avoid setState-in-render
    queueMicrotask(() => setDismissed(true));
  }

  // Confetti pieces -- 80 randomly-styled spans.  Memoised so the
  // colours/positions don't reshuffle every render; the dep is
  // deliberate (new celebration -> new confetti pattern).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const confetti = useMemo(() => makeConfetti(80), [celebration.startedAt]);

  if (!celebration.active || dismissed) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Weekly celebration"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(5, 8, 16, 0.78)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        overflow: 'hidden',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div aria-hidden style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {confetti.map((c, i) => (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${c.left}%`,
              top: '-12px',
              width: c.size,
              height: c.size * 0.4,
              background: c.color,
              borderRadius: 2,
              opacity: 0.9,
              animation: `confettiFall ${c.duration}s linear ${c.delay}s infinite`,
              transform: `rotate(${c.rotation}deg)`,
            }}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss celebration (Esc)"
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: '#21262d',
          color: '#e6edf3',
          border: '1px solid #30363d',
          borderRadius: 6,
          padding: '6px 12px',
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        ✕ close
      </button>

      <header style={{ textAlign: 'center', marginBottom: 28, position: 'relative' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 28,
            letterSpacing: 0.4,
            color: '#ffd33d',
            textShadow: '0 0 12px rgba(255, 211, 61, 0.6)',
          }}
        >
          🏆 Friday Celebration
        </h2>
        {celebration.winnerName ? (
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#c9d1d9' }}>
            This week's leader:{' '}
            <strong style={{ color: '#e6edf3' }}>
              {celebration.winnerName ?? celebration.winnerPodId}
            </strong>
            {celebration.metric && celebration.value != null ? (
              <>
                {' '}with{' '}
                <strong style={{ color: '#2ea043' }}>
                  {celebration.metric} = {celebration.value.toFixed(3)}
                </strong>
              </>
            ) : null}
          </p>
        ) : null}
      </header>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 18,
          position: 'relative',
          minHeight: 220,
        }}
      >
        {PODIUM_ORDER.map((rank) => {
          const entry = celebration.podium.find((p) => p.rank === rank);
          if (!entry) {
            return (
              <PodiumBlock
                key={rank}
                rank={rank}
                name={null}
                value={null}
                placeholder
              />
            );
          }
          return (
            <PodiumBlock
              key={rank}
              rank={rank}
              name={entry.name ?? entry.pod_id}
              value={entry.value}
            />
          );
        })}
      </div>

      <p
        style={{
          marginTop: 24,
          fontSize: 11,
          color: '#6e7681',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        Esc to dismiss · auto-closes in 60 s
      </p>
    </div>
  );
}

function PodiumBlock({
  rank,
  name,
  value,
  placeholder = false,
}: {
  rank: 1 | 2 | 3;
  name: string | null;
  value: number | null;
  placeholder?: boolean;
}) {
  const height = PODIUM_HEIGHT[rank];
  const bg = PODIUM_BG[rank];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 130,
        opacity: placeholder ? 0.45 : 1,
      }}
    >
      <div
        style={{
          background: '#0d1117',
          border: '1px solid #30363d',
          borderRadius: 10,
          padding: '8px 14px',
          marginBottom: 12,
          minWidth: 140,
          textAlign: 'center',
          fontSize: 13,
          color: '#e6edf3',
          minHeight: 56,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        <strong style={{ fontSize: 14 }}>{name ?? '—'}</strong>
        {value != null ? (
          <span style={{ fontSize: 12, color: value >= 0 ? '#2ea043' : '#f85149' }}>
            {value >= 0 ? '+' : ''}
            {value.toFixed(3)}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: '#6e7681' }}>awaiting…</span>
        )}
      </div>
      <div
        style={{
          width: 140,
          height,
          background: bg,
          borderRadius: '10px 10px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 36,
          fontWeight: 800,
          color: '#0d1117',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        }}
      >
        {rank}
      </div>
    </div>
  );
}

interface ConfettiPiece {
  left: number;
  size: number;
  color: string;
  duration: number;
  delay: number;
  rotation: number;
}

const CONFETTI_COLORS = [
  '#ffd33d',
  '#1f6feb',
  '#2ea043',
  '#db61a2',
  '#a371f7',
  '#f85149',
];

function makeConfetti(n: number): ConfettiPiece[] {
  const out: ConfettiPiece[] = [];
  for (let i = 0; i < n; i++) {
    const colorIdx = Math.floor(Math.random() * CONFETTI_COLORS.length);
    out.push({
      left: Math.random() * 100,
      size: 6 + Math.random() * 8,
      color: CONFETTI_COLORS[colorIdx] ?? '#ffd33d',
      duration: 3 + Math.random() * 3,
      delay: Math.random() * 2,
      rotation: Math.random() * 360,
    });
  }
  return out;
}
